import { Dispatch, SetStateAction, useMemo, useState, useEffect, useRef } from 'react'
import { Card, Chip, Button, Select, SelectItem } from '@heroui/react'
import { Icon } from '@iconify/react'
import { EngineAiOutput } from '@interfaces/ai'
import RiskCard from './RiskCard'
import { useTemporaryStore } from '@/contexts/temporaryStore'
import { useParams } from 'react-router-dom'
import { ai } from '@/services/ai/ai-service'
import { cn } from '@/lib/utils'

const DEFAULT_RISKS_LIMIT = 30

export interface EnhancedReviewPaneProps {
  output: EngineAiOutput
  blocksOrderData: string[]
  versionNumber: number
  setAiOutput: Dispatch<SetStateAction<EngineAiOutput | undefined>>
  reviewInProgress: boolean
}

// Single source of truth for risk level logic - used by both parent and child components
export const getRiskFactorProperties = (riskFactor: number) => {
  if (riskFactor <= 1) {
    return {
      level: 'INFORMATIONAL' as const,
      label: 'OTHER',
      color: 'primary' as const,
      dotColor: 'bg-blue-400',
    }
  } else if (riskFactor <= 4) {
    return {
      level: 'LOW' as const,
      label: 'LOW',
      color: 'success' as const,
      dotColor: 'bg-green-500',
    }
  } else if (riskFactor <= 7) {
    return {
      level: 'MEDIUM' as const,
      label: 'MEDIUM',
      color: 'warning' as const,
      dotColor: 'bg-yellow-500',
    }
  } else {
    return {
      level: 'HIGH' as const,
      label: 'HIGH',
      color: 'danger' as const,
      dotColor: 'bg-red-500',
    }
  }
}

const riskLevelOptions = [
  { key: 'ALL', label: 'All' },
  { key: 'HIGH', label: 'High' },
  { key: 'MEDIUM', label: 'Medium' },
  { key: 'LOW', label: 'Low' },
  {
    key: 'INFORMATIONAL',
    label: 'Other',
  },
] as const

const statusOptions = [
  { key: 'ALL', label: 'All' },
  { key: 'PENDING', label: 'Pending' },
  { key: 'APPLIED', label: 'Applied' },
  { key: 'DISMISSED', label: 'Dismissed' },
] as const

const documentTypeSpecialWord = {
  contract: 'risks',
  proposal: 'improvements',
  letter: 'issues',
  policy: 'issues',
  generic: 'issues',
} as const

const RiskAnalysisResults = (props: EnhancedReviewPaneProps) => {
  const { id: documentPublicId } = useParams()
  const [visibleInformationalCount, setVisibleInformationalCount] = useState(DEFAULT_RISKS_LIMIT)
  const [focusedRiskIndex, setFocusedRiskIndex] = useState<number | null>(null)

  const { riskResultsFilters, setRiskResultsFilters } = useTemporaryStore(s => ({
    riskResultsFilters: s.riskResultsFilters,
    setRiskResultsFilters: s.setRiskResultsFilters,
  }))

  const specialWord = documentTypeSpecialWord[props.output?.documentType || 'generic']
  const risksContainerRef = useRef<HTMLDivElement>(null)

  // Get the appropriate message when no risks are shown
  const getEmptyRisksMessage = () => {
    if (props.output.riskCards.length === 0) return 'Everything looks good here! 🎉'

    return `No cards match your current filters. Try adjusting the filters or click on "Reset" button to see all cards.`
  }

  // Split cards into priority and informational at the entry point
  const { priorityCards, informationalCards } = useMemo(() => {
    const priority = props.output.riskCards.filter(
      card => getRiskFactorProperties(card.riskFactor).level !== 'INFORMATIONAL'
    )
    const informational = props.output.riskCards.filter(
      card => getRiskFactorProperties(card.riskFactor).level === 'INFORMATIONAL'
    )
    return { priorityCards: priority, informationalCards: informational }
  }, [props.output.riskCards])

  const { filteredReviewRisks, totalFilteredCount, hasMoreToLoad } = useMemo(() => {
    // Determine which card pool to use based on risk level filter
    const isShowingInformational = riskResultsFilters.riskLevel === 'INFORMATIONAL'
    let filtered = isShowingInformational ? informationalCards : priorityCards

    // Filter by status if not 'ALL' and not showing informational (informational cards bypass status filter)
    if (!isShowingInformational && riskResultsFilters.status !== 'ALL')
      filtered = filtered.filter(card => card.status === riskResultsFilters.status)

    // Filter by risk level if not 'ALL' and not showing informational (since informational is already filtered)
    if (!isShowingInformational && riskResultsFilters.riskLevel !== 'ALL')
      filtered = filtered.filter(
        card => getRiskFactorProperties(card.riskFactor).level === riskResultsFilters.riskLevel
      )

    // Filter by target block if specified
    if (riskResultsFilters.targetBlock)
      filtered = filtered.filter(card => card.blockId === riskResultsFilters.targetBlock)

    const totalCount = filtered.length

    // Priority cards are always shown in full (no lazy loading)
    // Only informational cards get lazy loaded
    if (!isShowingInformational) {
      return { filteredReviewRisks: filtered, totalFilteredCount: totalCount, hasMoreToLoad: false }
    }

    // For informational cards: apply progressive lazy loading
    // During review (streaming), show all that have arrived so far
    if (props.reviewInProgress) {
      return { filteredReviewRisks: filtered, totalFilteredCount: totalCount, hasMoreToLoad: false }
    }

    // Show cards up to visibleInformationalCount
    const visibleCards = filtered.slice(0, visibleInformationalCount)
    const hasMore = filtered.length > visibleInformationalCount

    return { filteredReviewRisks: visibleCards, totalFilteredCount: totalCount, hasMoreToLoad: hasMore }
  }, [priorityCards, informationalCards, riskResultsFilters, props.reviewInProgress, visibleInformationalCount])

  // update the original output with the applied improvement, so that the real-time disabling of "apply all improvements" button works
  async function handleStatusChange(newCard: EngineAiOutput['riskCards'][number]) {
    // update UI to reflect the status change of this card
    props.setAiOutput(prev => {
      if (!prev) return prev

      return {
        ...prev,
        riskCards: prev.riskCards.map(e =>
          e.blockId === newCard.blockId && e.suggestion === newCard.suggestion ? newCard : e
        ),
      }
    })

    // status change should be propagated to the backend as well
    ai.updateRiskCardStatus({
      documentPublicId: documentPublicId || '',
      documentVersionNumber: props.versionNumber,
      blockId: newCard.blockId,
      status: newCard.status as EngineAiOutput['riskCards'][number]['status'],
      suggestion: newCard.suggestion,
    })
  }

  // Keyboard navigation for risk cards
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle keyboard navigation if we have risks
      if (filteredReviewRisks.length === 0) return
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return
      e.preventDefault()

      // Remove native browser focus to avoid conflicts
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur()

      if (e.key === 'ArrowDown') {
        setFocusedRiskIndex(prev => {
          // If no card is focused, focus the first one
          if (prev === null) return 0
          // Otherwise, move down but stop at the last card
          return Math.min(prev + 1, filteredReviewRisks.length - 1)
        })
      } else if (e.key === 'ArrowUp') {
        setFocusedRiskIndex(prev => {
          // If no card is focused, do nothing
          if (prev === null) return null
          // Move up but stop at the first card
          const newIndex = Math.max(prev - 1, 0)
          // If we're at 0 and pressing up, unfocus
          if (prev === 0) return null
          return newIndex
        })
      } else if (e.key === 'Enter') {
        // If a card is focused, trigger the apply improvement button
        if (focusedRiskIndex !== null) {
          e.preventDefault()
          const focusedCard = document.querySelector(`[data-risk-index="${focusedRiskIndex}"]`)
          const applyBtn = focusedCard?.querySelector('[data-apply-btn]') as HTMLButtonElement
          if (applyBtn && !applyBtn.disabled) {
            applyBtn.click()
          }
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [filteredReviewRisks.length, focusedRiskIndex])

  // Clear focus when clicking outside the risks container or outside any risk card
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!risksContainerRef.current) return
      if (!(e.target instanceof HTMLElement)) return

      const isInsideContainer = risksContainerRef.current.contains(e.target)
      const isInsideRiskCard = !!e.target.closest('[data-risk-index]')

      // If click is outside container OR inside container but not on a card, clear focus
      if (!isInsideContainer || !isInsideRiskCard) {
        setFocusedRiskIndex(null)
      }
    }

    window.addEventListener('click', handleClick)
    return () => window.removeEventListener('click', handleClick)
  }, [])

  // Scroll focused card into view and click it
  useEffect(() => {
    if (focusedRiskIndex !== null) {
      const focusedCard = document.querySelector(`[data-risk-index="${focusedRiskIndex}"]`) as HTMLElement
      if (focusedCard) {
        // Click the card to trigger scroll to content
        focusedCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
        focusedCard.click()
      }
    }
  }, [focusedRiskIndex])

  return (
    <div className="grid gap-4" ref={risksContainerRef}>
      {/* Filters Section */}
      <div
        className={cn(
          'sticky top-0 z-20 flex w-full flex-wrap items-center gap-3 px-4 py-3',
          '-ms-4 w-[calc(100%+32px)] bg-[#f8f9fb] shadow-[0_2px_8px_-2px_rgba(0,0,0,0.1)]'
        )}
      >
        <div className="flex w-full flex-wrap items-center gap-[0.5rem]">
          {/* Status Filter Chip */}
          <Select
            selectedKeys={[riskResultsFilters.status]}
            onSelectionChange={keys => {
              const selected = Array.from(keys)[0] as string
              setRiskResultsFilters({ status: selected as any })
            }}
            isDisabled={riskResultsFilters.riskLevel === 'INFORMATIONAL'}
            className="w-fit min-w-[11rem]"
            classNames={{
              trigger: 'min-h-7 h-auto bg-gray-100 border border-gray-300 rounded-full px-2.5 hover:bg-gray-200',
              value: 'text-sm font-medium text-gray-700 flex items-center gap-2',
              selectorIcon: 'text-gray-500',
            }}
            renderValue={() => (
              <>
                <span className="text-xs text-gray-500">Status</span>
                <span className="text-sm font-medium text-gray-300">|</span>
                <span className="text-sm font-medium text-lime-600">
                  {statusOptions.find(opt => opt.key === riskResultsFilters.status)?.label || 'All'}
                  {/* (
                    {riskResultsFilters.status === 'ALL'
                      ? priorityCards.length
                      : priorityCards.filter(card => card.status === riskResultsFilters.status).length}
                    ) */}
                </span>
              </>
            )}
          >
            {statusOptions.map(option => (
              <SelectItem key={option.key}>
                {option.label} (
                {option.key === 'ALL'
                  ? priorityCards.length
                  : priorityCards.filter(card => card.status === option.key).length}
                )
              </SelectItem>
            ))}
          </Select>

          {/* Risk Level Filter Chip */}
          <Select
            selectedKeys={[riskResultsFilters.riskLevel]}
            onSelectionChange={keys => {
              const selected = Array.from(keys)[0] as string
              setRiskResultsFilters({ riskLevel: selected as any })
              // Reset visible count when switching filters
              setVisibleInformationalCount(DEFAULT_RISKS_LIMIT)
            }}
            className="w-fit min-w-[10rem]"
            classNames={{
              trigger: 'min-h-7 h-auto bg-gray-100 border border-gray-300 rounded-full px-2.5 hover:bg-gray-200',
              value: 'text-sm font-medium text-gray-700 flex items-center gap-2',
              selectorIcon: 'text-gray-500',
            }}
            renderValue={() => (
              <>
                <span className="text-xs text-gray-500">Level</span>
                <span className="text-sm font-medium text-gray-300">|</span>
                <span className="text-sm font-medium text-lime-600">
                  {riskLevelOptions.find(opt => opt.key === riskResultsFilters.riskLevel)?.label || 'All'}
                  {/* (
                    {riskResultsFilters.riskLevel === 'ALL'
                      ? priorityCards.length
                      : riskResultsFilters.riskLevel === 'INFORMATIONAL'
                        ? informationalCards.length
                        : priorityCards.filter(
                            card => getRiskFactorProperties(card.riskFactor).level === riskResultsFilters.riskLevel
                          ).length}
                    ) */}
                </span>
              </>
            )}
          >
            {riskLevelOptions
              .filter(option => option.key !== 'INFORMATIONAL' || informationalCards.length > 0)
              .map(option => (
                <SelectItem key={option.key}>
                  {option.label} (
                  {option.key === 'ALL'
                    ? priorityCards.length
                    : option.key === 'INFORMATIONAL'
                      ? informationalCards.length
                      : priorityCards.filter(card => getRiskFactorProperties(card.riskFactor).level === option.key)
                          .length}
                  )
                </SelectItem>
              ))}
          </Select>

          {/* Reset Filters Button */}
          <Button
            size="sm"
            variant="flat"
            onPress={() => {
              setRiskResultsFilters({ status: 'PENDING', riskLevel: 'ALL', targetBlock: null })
              setVisibleInformationalCount(DEFAULT_RISKS_LIMIT)
            }}
            className="ms-auto h-7 min-w-fit rounded-full border border-gray-300 bg-gray-100 px-2 py-1 hover:bg-gray-100"
          >
            <div className="flex items-center gap-2">
              <Icon icon="heroicons:arrow-path" className="min-h-3.5 min-w-3.5 text-gray-500" />
              <span className="text-xs text-gray-500">Reset</span>
            </div>
          </Button>
        </div>

        {/* Target Block Filter Chip */}
        {riskResultsFilters.targetBlock && (
          <div className="flex h-7 items-center gap-2 rounded-full border border-gray-300 bg-gray-100 px-2.5 py-1">
            <span className="text-xs text-gray-500">Filtered by</span>
            <span className="text-sm font-medium text-gray-300">|</span>
            <span className="text-sm font-medium text-lime-600">Clause</span>
            <button
              onClick={() => setRiskResultsFilters({ targetBlock: null })}
              className="ml-1 rounded-full p-1 hover:bg-gray-200"
            >
              <Icon icon="heroicons:x-mark" className="h-4 w-4 text-gray-500 hover:text-gray-700" />
            </button>
          </div>
        )}
      </div>

      {/* summary and review cards */}
      <div
        className="mt-0 grid gap-4"
        key={`${riskResultsFilters.status}-${riskResultsFilters.riskLevel}-${riskResultsFilters.targetBlock}`}
      >
        {props.output.documentSummary && (
          <Card className="review-summary grid gap-2 rounded-md bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2">
              <Chip variant="flat" className="bg-lime-100">
                <span className="font-medium text-lime-600">Document summary</span>
              </Chip>
            </div>
            <p className="pt-3 text-small text-default-600">{props.output.documentSummary}</p>
          </Card>
        )}

        {filteredReviewRisks.length === 0 && (
          <Card className="text-sm shadow-small">
            {props.reviewInProgress ? (
              <p className="animate-pulse p-5 text-center text-default-600">
                No {specialWord} found so far. Hang tight...
              </p>
            ) : (
              <p className="p-5 text-center text-default-600">{getEmptyRisksMessage()}</p>
            )}
          </Card>
        )}

        {/* risk cards list */}
        {filteredReviewRisks.map((risk, index) => (
          <RiskCard
            key={risk.blockId + index}
            risk={risk}
            versionNumber={props.versionNumber}
            documentSummary={props.output.documentSummary}
            onStatusChange={handleStatusChange}
            isFocused={focusedRiskIndex === index}
            riskIndex={index}
            reviewInProgress={props.reviewInProgress}
            onCardClick={clickedIndex => setFocusedRiskIndex(clickedIndex)}
          />
        ))}

        {/* load more button */}
        {riskResultsFilters.riskLevel === 'INFORMATIONAL' && hasMoreToLoad && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="bordered"
              onPress={() => setVisibleInformationalCount(prev => prev + DEFAULT_RISKS_LIMIT)}
              startContent={<Icon icon="heroicons:chevron-down" className="text-lg" />}
              className="font-medium"
            >
              Show {Math.min(DEFAULT_RISKS_LIMIT, totalFilteredCount - filteredReviewRisks.length)} more (
              {totalFilteredCount - filteredReviewRisks.length} remaining)
            </Button>
          </div>
        )}

        {/* Informational Cards Hint - Show when not viewing informational cards */}
        {riskResultsFilters.riskLevel !== 'INFORMATIONAL' && informationalCards.length > 0 && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="bordered"
              onPress={() => {
                setRiskResultsFilters({ riskLevel: 'INFORMATIONAL' })
                setVisibleInformationalCount(DEFAULT_RISKS_LIMIT)
              }}
              startContent={<Icon icon="heroicons:information-circle" className="text-lg" />}
              className="font-medium"
            >
              Show other {specialWord} ({informationalCards.length})
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default RiskAnalysisResults
